// Where the demo venue lives, and how the console tab and the kiosk tab share it (spec/24 §2.3, §2.4).
//
// One JSON value in the browser's `localStorage`, under a key made of the signed-in user and the start of their demo
// window, and a `BroadcastChannel` message on every change. A tab reads the stored venue, applies an action to it,
// writes it back and says so; every other tab of the same visitor hears it and reads again. A new window is a new
// key, so a demo never carries the last one's venue, and keys of earlier windows are removed.
//
// The storage and the channel are handed in — the browser's in the apps, plain objects in the tests — and the clock
// is a parameter, so nothing here reaches for a global (the `pure` lint preset holds it to that). A store that
// cannot write (a private window, a full quota) keeps the venue in memory for that tab and goes on.
import { advance, createDemoState } from "./engine.js";
/** Every key the demo writes starts with this. */
export const DEMO_STORAGE_PREFIX = 'clavian.demo.v1.';
/** The channel every demo tab of one browser listens on. */
export const DEMO_CHANNEL_NAME = 'clavian-demo';
/** The venue's key for one person's demo window: a new window is a new venue (spec/24 §2.4). */
export function demoStorageKey(userId, windowStartedAt) {
    return `${DEMO_STORAGE_PREFIX}${userId}.${windowStartedAt}`;
}
/** Remove every demo venue but `keep`'s — the earlier windows' (spec/24 §2.4). Returns how many went. */
export function forgetOtherDemos(storage, keep) {
    const stale = [];
    for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key !== null && key.startsWith(DEMO_STORAGE_PREFIX) && key !== keep)
            stale.push(key);
    }
    for (const key of stale) {
        try {
            storage.removeItem(key);
        }
        catch {
            // A storage that refuses a removal keeps a stale venue nobody reads.
        }
    }
    return stale.length;
}
const isNotice = (value) => typeof value === 'object' && value !== null && value.kind === 'clavian-demo-changed' && typeof value.key === 'string';
function parse(raw) {
    if (raw === null)
        return undefined;
    try {
        const value = JSON.parse(raw);
        // A value from an older shape, or one somebody edited by hand, is not trusted: the venue opens fresh.
        if (value === null || value.version !== 1 || typeof value.epoch !== 'number' || !Array.isArray(value.sessions) || !Array.isArray(value.orders)) {
            return undefined;
        }
        return value;
    }
    catch {
        return undefined;
    }
}
export function createDemoStore(options) {
    const { key, storage, channel, now } = options;
    const listeners = new Set();
    // What this tab last read or wrote: the venue when storage cannot hold it.
    let memory;
    const save = (state) => {
        memory = state;
        try {
            storage.setItem(key, JSON.stringify(state));
        }
        catch {
            // Private mode or a full quota: this tab keeps the venue in memory and the other tab cannot see it.
        }
    };
    const notify = (state) => {
        for (const listener of [...listeners])
            listener(state);
    };
    const announce = (state) => {
        try {
            channel?.postMessage({ kind: 'clavian-demo-changed', key, revision: state.revision });
        }
        catch {
            // A closed channel: other tabs catch up on their next read.
        }
    };
    const stored = () => {
        let raw;
        try {
            raw = storage.getItem(key);
        }
        catch {
            // Storage the browser refuses (a private window, blocked site data): the tab keeps its venue in memory.
            raw = null;
        }
        return parse(raw) ?? memory;
    };
    const read = () => {
        const at = now();
        const current = stored() ?? createDemoState(at);
        const advanced = advance(current, at);
        if (advanced !== current || stored() === undefined) {
            const next = advanced === current ? current : { ...advanced, revision: current.revision + 1 };
            save(next);
            if (advanced !== current)
                announce(next);
            return next;
        }
        memory = current;
        return current;
    };
    const onMessage = (event) => {
        if (!isNotice(event.data) || event.data.key !== key)
            return;
        notify(read());
    };
    channel?.addEventListener('message', onMessage);
    return {
        key,
        read,
        update(change) {
            const at = now();
            const current = read();
            const changed = change(current, at);
            if (changed === current)
                return current;
            const next = { ...changed, revision: current.revision + 1 };
            save(next);
            announce(next);
            notify(next);
            return next;
        },
        reset() {
            const current = stored();
            const fresh = advance(createDemoState(now()), now());
            const next = { ...fresh, revision: (current?.revision ?? 0) + 1 };
            save(next);
            announce(next);
            notify(next);
            return next;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        close() {
            listeners.clear();
            channel?.removeEventListener('message', onMessage);
        },
    };
}
//# sourceMappingURL=store.js.map